import { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import dynamic from 'next/dynamic';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import useTranslation from '@/utils/hooks/translation/useTranslation';

import { Trans } from '@/components/tools/translation';

dayjs.extend(utc);
dayjs.extend(timezone);

// Hooks
import useGroupMedias from '@/utils/hooks/swr/group/useGroupMedias';
import useSPGroups from '@/utils/hooks/swr/group/useSPGroups';
import useUser from '@/utils/hooks/swr/useUser';
import useAcl from '@/utils/hooks/user/useAcl';
import useActiveGroup from '@/utils/hooks/user/useActiveGroup';

// Lib
import faqUrl from '@/utils/urls/faqUrl';
import useLabelsAlbumsOnMedia from '@/utils/hooks/utils/useLabelsAlbumsOnMedia';

import { aclFeatures } from '@/lib/acl/aclFeatures';
import { GROUP, postSortOptions } from '@/lib/posts/postSortOptions';
import { multiNetworksPostsSortTypes } from '@/lib/posts/postSortTypes';
import { POST } from '@/lib/posts/postTypes';
import { TIKTOK_BUSINESS } from '@/lib/socialProfile';
import { url_faq_estimated_followers } from '@/lib/urls/faqUrls';

import Carousel from '@/components/base/Carousel';
// Components
import LinkBase from '@/components/base/LinkBase';
import { PostComponent } from '@/components/base/Post/PostComponent';
import { WidgetMedia } from '@/components/base/WidgetMedia';
import CustomDashboardWidgetActions from '@/components/pages/custom-dashboards/dashboard/CustomDashboardWidgetActions';
const CustomDashboardWidgetOptionSaver = dynamic(
    () => import('@/components/pages/custom-dashboards/dashboard/CustomDashboardWidgetOptionSaver')
);

// Composites
const MediaViewerModal = dynamic(
    () => import('@/components/composite/MediaViewer/MediaViewerModal')
);

export const GroupMediasWidget = ({
    periodData,
    sortFields,
    page,
    numberFormat,
    onDelete,
    config,
    isDashboard = false
}) => {
    const { t } = useTranslation('commons');

    const { user } = useUser();

    const { hasAcl } = useAcl();

    const { activeGroup } = useActiveGroup();

    const { activatedSpGroups } = useSPGroups({ revalidateIfStale: false });

    const [mediaViewerParams, setMediaViewerParams] = useState({
        isOpen: false,
        mediaId: null,
        group: null,
        socialProfile: null
    });

    const [groupSelected, setGroupSelected] = useState(
        isDashboard ? activatedSpGroups.find((group) => group.id === config.groups[0]) : null
    );

    const currentGroup = isDashboard ? groupSelected : activeGroup;

    const networks = config.aggregatedNetworks.reduce(
        (networkTypes, networkType) =>
            currentGroup.social_profiles.findIndex((sp) => sp.social_network_type === networkType) <
            0
                ? networkTypes
                : [...networkTypes, networkType],
        []
    );

    const sortOptions = postSortOptions(t, page)
        ?.[GROUP]?.filter(
            (element) =>
                sortFields?.includes(element?.value) &&
                (!element.notAvailableWithNetworks ||
                    element.notAvailableWithNetworks?.filter((network) =>
                        networks.includes(network)
                    ).length === 0)
        )
        ?.sort((a, b) => sortFields?.indexOf(a?.value) - sortFields?.indexOf(b?.value));

    const sortTypes = multiNetworksPostsSortTypes(t, networks);

    const {
        dataMedias,
        requestParams,
        mediasError,
        isLoadingInitialData,
        mutateMedias,
        setRequestParams
    } = useGroupMedias({
        groupId: currentGroup?.id,
        limit: 10
    });

    const { handleAlbumsEvent, handleLabelsEvent } = useLabelsAlbumsOnMedia({
        mutateMedias,
        requestParams
    });

    const formatDate = (dateToFormat) => {
        if (!dateToFormat) return null;
        return dayjs.tz(dateToFormat, user.settings.timezone).toISOString();
    };

    useEffect(() => {
        if (periodData?.date?.[0] && periodData?.date?.[1]) {
            setRequestParams((prevState) => ({
                ...prevState,
                start: formatDate(periodData.date[0]),
                end: formatDate(periodData.date[1]),
                with_labels: true,
                with_albums: true
            }));
        }
    }, [periodData.period, periodData.date]);

    // relaunch request if active group changes
    useEffect(() => {
        if (
            sortTypes?.filter((el) => requestParams?.filter_post_type?.includes(el?.value))
                ?.length === 0
        ) {
            setRequestParams((prevState) => ({
                ...prevState,
                filter_post_type: []
            }));
        }

        if (
            sortOptions?.filter((el) => {
                return requestParams?.sort?.column === el?.value;
            })?.length === 0
        ) {
            setRequestParams((prevState) => ({
                ...prevState,
                sort: {
                    column: sortOptions?.[0]?.value,
                    order: sortOptions?.[0]?.order
                }
            }));
        }
    }, [currentGroup?.id]);

    useEffect(() => {
        if (config?.parameters) {
            setRequestParams((prevState) => ({
                ...prevState,
                ...config.parameters
            }));
        }
    }, []);

    return (
        <WidgetMedia>
            <WidgetMedia.Header>
                <div className="flex items-center space-x-3">
                    {isDashboard ? (
                        <>
                            <div>
                                <CustomDashboardWidgetActions.SelectGroup
                                    config={config}
                                    groups={config.groups}
                                    setGroupSelected={setGroupSelected}
                                    isMedia
                                />
                            </div>
                            <CustomDashboardWidgetOptionSaver>
                                {(saveWidgetOption) => (
                                    <>
                                        <div className="w-52">
                                            <WidgetMedia.SortBy
                                                setRequestParams={setRequestParams}
                                                data={sortOptions}
                                                currentSort={
                                                    config?.parameters
                                                        ? config?.parameters?.sort
                                                        : requestParams?.sort
                                                }
                                                onChange={(sort) => {
                                                    saveWidgetOption(
                                                        config,
                                                        'sort',
                                                        {
                                                            order: sort.order,
                                                            column: sort.value
                                                        },
                                                        {
                                                            order: sortOptions[0].order,
                                                            column: sortOptions[0].value
                                                        }
                                                    );
                                                }}
                                            />
                                        </div>
                                        {config?.noSortTypesSelect ? null : (
                                            <div className="w-36">
                                                <WidgetMedia.SelectType
                                                    setRequestParams={setRequestParams}
                                                    currentFilter={
                                                        config?.parameters
                                                            ? config?.parameters?.filter_post_type
                                                            : requestParams?.filter_post_type
                                                    }
                                                    data={sortTypes}
                                                    onChange={(filter_post_type) =>
                                                        saveWidgetOption(
                                                            config,
                                                            'filter_post_type',
                                                            filter_post_type,
                                                            []
                                                        )
                                                    }
                                                />
                                            </div>
                                        )}
                                    </>
                                )}
                            </CustomDashboardWidgetOptionSaver>
                        </>
                    ) : (
                        <>
                            <div className="w-52">
                                <WidgetMedia.SortBy
                                    setRequestParams={setRequestParams}
                                    data={sortOptions}
                                />
                            </div>
                            {config?.noSortTypesSelect ? null : (
                                <div className="w-36">
                                    <WidgetMedia.SelectType
                                        setRequestParams={setRequestParams}
                                        data={sortTypes}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>
                {isDashboard ? (
                    <CustomDashboardWidgetActions.Popover className="bg-white dark:bg-icoDarkMode-deepBlack">
                        <li className="rounded font-GMedium text-xs text-icoGray-500 transition-all hover:bg-icoBlue-100 hover:text-icoBlue dark:hover:bg-icoDarkMode-deepBlack">
                            <button className="w-full p-2 text-left" onClick={onDelete}>
                                {t('dashboard:remove_widget')}
                            </button>
                        </li>
                    </CustomDashboardWidgetActions.Popover>
                ) : null}
            </WidgetMedia.Header>
            {isLoadingInitialData && !dataMedias ? (
                <WidgetMedia.Placeholder
                    wrapperClass="xl:space-x-m 2xl:space-x-5"
                    postClass="min-w-[calc(33.3%-6px)] xl:min-w-[calc(25%-13.8px)] 2xl:min-w-[calc(20%-16px)]"
                    postFormat="square"
                />
            ) : null}
            {!isLoadingInitialData && dataMedias && dataMedias[0]?.medias?.length === 0 ? (
                <WidgetMedia.NoData
                    networks={networks}
                    message={t('analytics:no_posts_in_period')}
                />
            ) : null}

            {mediasError && !isLoadingInitialData ? (
                <WidgetMedia.ErrorFallBack resetErrorBoundary={() => mutateMedias()} />
            ) : null}

            {!isLoadingInitialData && dataMedias && dataMedias[0]?.medias?.length > 0 ? (
                <ErrorBoundary
                    FallbackComponent={WidgetMedia.ErrorFallBack}
                    onReset={() => mutateMedias()}>
                    <WidgetMedia.Carousel
                        navPosition="external"
                        className="xl:space-x-m 2xl:space-x-5">
                        {dataMedias?.map((medias) => {
                            return medias?.['medias']?.map((media, index) => {
                                // Disable promoted_post if the user doesn't have the ACL
                                let promoted_post = false;
                                if (hasAcl(aclFeatures.analytic.promoted_post.read)) {
                                    promoted_post = media?.promoted_post ?? false;
                                }
                                const socialProfile = currentGroup.social_profiles.find(
                                    (sp) => sp.id === media.social_profile_id
                                );

                                return (
                                    <Carousel.Slide
                                        key={media?.id}
                                        arial-label={`media ${media?.id}`}
                                        className="min-w-[calc(33.3%-6px)] max-w-[calc(33.3%-6px)] xl:min-w-[calc(25%-13.8px)] xl:max-w-[calc(25%-13.8px)] 2xl:min-w-[calc(20%-16px)] 2xl:max-w-[calc(20%-16px)]">
                                        <PostComponent
                                            data-testid={`post-${index}`}
                                            network={socialProfile?.social_network_type}
                                            type="medias"
                                            isLoading={false}
                                            data={{
                                                ...media,
                                                promoted_post,
                                                social_profile: socialProfile,
                                                username: socialProfile?.slug
                                            }}
                                            config={{
                                                postType: POST,
                                                postFormat: 'square',
                                                activeFilter: requestParams?.sort?.column ?? null,
                                                network: socialProfile?.social_network_type,
                                                numberFormat: numberFormat,
                                                forceSquareFormat:
                                                    networks.findIndex(
                                                        (network) => network !== TIKTOK_BUSINESS
                                                    ) >= 0
                                            }}
                                            handleLabelsEvent={handleLabelsEvent}
                                            handleAlbumsEvent={handleAlbumsEvent}
                                            openViewer={(e) => {
                                                setMediaViewerParams({
                                                    isOpen: true,
                                                    mediaId: media?.id,
                                                    socialProfile,
                                                    group: currentGroup
                                                });
                                                e.stopPropagation();
                                            }}
                                        />
                                    </Carousel.Slide>
                                );
                            });
                        })}
                    </WidgetMedia.Carousel>
                    {dataMedias[0]?.not_full_medias ? (
                        <div className="font-GMedium text-xs text-icoGray-500 dark:text-icoDarkMode-wolf">
                            <Trans
                                i18nKey="analytics:not_full_medias"
                                components={{
                                    tag0: (
                                        <LinkBase
                                            key="learn_more"
                                            target="_blank"
                                            className="underline"
                                            href={faqUrl(url_faq_estimated_followers)}
                                            rel="help noopener noreferrer"
                                        />
                                    )
                                }}
                            />
                        </div>
                    ) : null}
                </ErrorBoundary>
            ) : null}
            <MediaViewerModal
                isOpen={mediaViewerParams?.isOpen}
                toggle={() => {
                    setMediaViewerParams({ ...mediaViewerParams, isOpen: false });
                }}
                mediaId={mediaViewerParams?.mediaId}
                group={mediaViewerParams?.group}
                socialProfile={mediaViewerParams?.socialProfile}
                mediaType={POST}
                mutateMedias={mutateMedias}
                mediaRequestParams={requestParams}
            />
        </WidgetMedia>
    );
};

export default GroupMediasWidget;
